<template>
	<div v-if="loaded">
		<div class="d-flex align-items-center justify-content-between mb-5">
			<h5 class="header-case">
				{{ groupData.groupInfo.name }}
			</h5>
			<div>
				<strong>Group Owner - </strong>
				<span class="font-weight-bold text-primary"> {{ ownerName }}</span>
				<strong></strong>
			</div>
		</div>
		<div>
			<div class="metrics row">
				<div class="col-md-2 metric-wrapper border-right">
					<p class="metric-label">
						<strong>Teams</strong>
					</p>
					<p class="metric-number">
						{{ numberOfGroupTeams }}
					</p>
				</div>
				<div class="col-md-2 metric-wrapper border-right">
					<p class="metric-label">
						<strong>Users</strong>
					</p>
					<p class="metric-number">
						{{ numberOfGroupUsers }}
					</p>
				</div>
				<div class="col-md-2 metric-wrapper border-right">
					<p class="metric-label">
						<strong>Created At</strong>
					</p>
					<p class="metric-number">
						{{ groupCreationDate }}
					</p>
				</div>
			</div>
		</div>
		<div class="team-actions mt-4 d-flex justify-content-between">
			<div class="col px-2">
				<a
					href="#"
					class="tab border-bottom text-center d-block w-100 pb-3 light-text"
					:class="{ active: tab == 'subscription' }"
					@click.prevent="openTab('subscription')"
					>Subscription</a
				>
			</div>
			<div class="col px-2">
				<a
					href="#"
					class="tab border-bottom text-center d-block w-100 pb-3 light-text"
					:class="{ active: tab == 'features' }"
					@click.prevent="openTab('features')"
					>Features</a
				>
			</div>
			<div class="col px-2">
				<a
					href="#"
					class="tab border-bottom text-center d-block w-100 pb-3 light-text"
					:class="{ active: tab == 'members' }"
					@click.prevent="openTab('members')"
					>Members</a
				>
			</div>
			<div class="col px-2">
				<a
					href="#"
					class="tab border-bottom text-center d-block w-100 pb-3 light-text"
					:class="{ active: tab == 'teams' }"
					@click.prevent="openTab('teams')"
					>Teams</a
				>
			</div>
			<div class="col px-2">
				<a
					href="#"
					class="tab border-bottom text-center d-block w-100 pb-3 light-text"
					:class="{ active: tab == 'settings' }"
					@click.prevent="openTab('settings')"
					>Settings</a
				>
			</div>
		</div>
		<div class="pt-3">
			<component
				v-bind:is="currentTabComponent"
				:groupData="groupData"
				@update-triggered="update"
				@update-delete="deleteGroup"
			></component>
		</div>
	</div>
	<div v-else>
		<div class="loader d-block text-center">
			<p><i class="fas fa-circle-notch fa-spin"></i></p>
			<p>Loading..</p>
		</div>
	</div>
</template>

<script>
import AdministrationMixin from './AdministrationMixin';
import ManageGroupSubscription from './tabs/ManageGroupSubscription';
import ManageGroupFeatures from './tabs/ManageGroupFeatures.vue';
import ManageGroupMembers from './tabs/ManageGroupMembers.vue';
import ManageGroupTeams from './tabs/ManageGroupTeams.vue';
import ManageGroupSettings from './tabs/ManageGroupSettings.vue';

export default {
	components: {
		ManageGroupSubscription,
		ManageGroupFeatures,
		ManageGroupMembers,
		ManageGroupTeams,
		ManageGroupSettings,
	},
	props: {
		groupId: {
			type: Number,
			default: null,
		},
		partner: {
			type: Boolean,
			default: null,
		},
	},
	mixins: [AdministrationMixin],

	data() {
		return {
			groupData: {
				groupInfo: {},
				groupTeams: [],
				groupUsers: [],
				teamsToAdd: [],
				usersToAdd: [],
			},
			loaded: false,
			tab: 'subscription',
		};
	},
	mounted() {
		this.tryLoadGroupData();
	},
	watch: {
		groupId: function (newVal, oldVal) {
			if (newVal !== oldVal) {
				this.loaded = false;
				this.tryLoadGroupData();
			}
		},
	},
	computed: {
		currentTabComponent() {
			switch (this.tab) {
				case 'subscription': {
					return 'manage-group-subscription';
				}
				case 'settings': {
					return 'manage-group-settings';
				}
				case 'members': {
					return 'manage-group-members';
				}
				case 'teams': {
					return 'manage-group-teams';
				}
				case 'features': {
					return 'manage-group-features';
				}
			}

			return '';
		},
		ownerName() {
			const { groupUsers, usersToAdd, groupInfo } = this.groupData;
			const allUsers = [...groupUsers, ...usersToAdd];
			const owner = allUsers.find((user) => user.user_id === groupInfo.owner_id);
			return owner ? owner.user_name : 'Owner not found';
		},
		numberOfGroupTeams() {
			return this.groupData.groupTeams.length;
		},
		numberOfGroupUsers() {
			return this.groupData.groupUsers.length;
		},
		groupCreationDate() {
			const createdAt = new Date(this.groupData.groupInfo.created_at);
			const formattedDate = createdAt.toLocaleDateString('en-US', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
			});
			return formattedDate;
		},
	},
	methods: {
		openTab(tab) {
			this.tab = tab;
		},
		tryLoadGroupData() {
			if (this.groupId) {
				this.loadGroupData(this.groupId);
			}
		},
		loadGroupData(groupId) {
			axios
				.get(`/api/groups/${groupId}/team-and-user`)
				.then(({ data }) => {
					if (!data.group) {
						throw new Error('Problem loading Group data');
					}
					Object.assign(this.groupData, {
						groupInfo: data.group,
						groupTeams: data.teams,
						groupUsers: data.users,
						teamsToAdd: data.teams_to_add,
						usersToAdd: data.users_to_add,
					});
					this.loaded = true;
				})
				.catch((error) => {
					this.handleAxiosError('Problem loading Group data', error);
				});
		},
		deleteGroup() {
			this.$emit('update-group-id', true);
		},
		update() {
			this.loadGroupData(this.groupId);
			this.$emit('update-all', true);
		},
	},
};
</script>

<style scoped>
::v-deep small {
	font-size: 1em !important;
}
.tab {
	border-bottom-width: 2px;
}
.tab.active {
	color: #00aef1;
	border-bottom: solid 2px #00aef1 !important;
}
</style>
